/**
 * CertificationGate.tsx — Teacher-Ready Output Certification Modal
 *
 * Shown before any export (print, PPTX, PDF) when the certification engine
 * finds blocking or warning failures.
 *
 * Layout:
 *   • Blockers listed first (red) — export is locked until teacher acknowledges
 *   • Warnings listed second (amber) — informational; teacher may proceed
 *   • "Export Anyway" bypass button — available when all failures are warn-only
 *     OR when the teacher explicitly overrides a block (advanced bypass)
 *   • "Close" — cancel the export entirely
 */

import React from 'react';
import { AlertTriangle, XCircle, CheckCircle2, X, ShieldAlert, ShieldCheck } from 'lucide-react';
import type { CertificationReport, CheckResult } from './certification';

interface Props {
  report: CertificationReport;
  resourceLabel: string; // e.g. "Activity Sheet", "Presentation", "Assessment"
  onExportAnyway: () => void;
  onClose: () => void;
}

function CheckRow({ result }: { result: CheckResult }) {
  const isBlock = result.severity === 'block';
  return (
    <div className={`flex items-start gap-3 py-3 border-b last:border-b-0 ${
      isBlock ? 'border-red-100' : 'border-amber-100'
    }`}>
      <div className="flex-shrink-0 mt-0.5">
        {isBlock
          ? <XCircle size={15} className="text-red-500" />
          : <AlertTriangle size={15} className="text-amber-500" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold mb-0.5 ${isBlock ? 'text-red-800' : 'text-amber-800'}`}>
          {result.checkName}
        </p>
        <p className={`text-xs leading-relaxed ${isBlock ? 'text-red-700' : 'text-amber-700'}`}>
          {result.message}
        </p>
        {result.detail && (
          <p className="text-xs text-slate-500 mt-1 italic">{result.detail}</p>
        )}
      </div>
    </div>
  );
}

export function CertificationGate({ report, resourceLabel, onExportAnyway, onClose }: Props) {
  const [showAdvancedBypass, setShowAdvancedBypass] = React.useState(false);

  // warn-only → teacher can export without enabling advanced bypass
  const canExportDirectly = report.passed; // no blockers

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 print:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cert-modal-title"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className={`px-5 py-4 flex items-start gap-3 border-b ${
          report.passed ? 'bg-amber-50 border-amber-100' : 'bg-red-50 border-red-100'
        }`}>
          <div className="flex-shrink-0 mt-0.5">
            {report.passed
              ? <ShieldAlert size={20} className="text-amber-500" />
              : <ShieldAlert size={20} className="text-red-500" />
            }
          </div>
          <div className="flex-1">
            <h2 id="cert-modal-title" className={`text-sm font-bold ${report.passed ? 'text-amber-800' : 'text-red-800'}`}>
              {report.passed
                ? `${resourceLabel} — Review before exporting`
                : `${resourceLabel} export blocked`
              }
            </h2>
            <p className={`text-xs mt-0.5 ${report.passed ? 'text-amber-600' : 'text-red-600'}`}>
              {report.passed
                ? `${report.warnings.length} warning${report.warnings.length !== 1 ? 's' : ''} found. You may export after reviewing.`
                : `${report.blockers.length} issue${report.blockers.length !== 1 ? 's' : ''} must be resolved before this resource can be exported.`
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Failure list ── */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {report.blockers.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-red-500 mb-2">
                Must fix before export ({report.blockers.length})
              </p>
              {report.blockers.map(r => <CheckRow key={r.checkId} result={r} />)}
            </div>
          )}

          {report.warnings.length > 0 && (
            <div className={report.blockers.length > 0 ? 'mt-4' : ''}>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-500 mb-2">
                Advisories ({report.warnings.length})
              </p>
              {report.warnings.map(r => <CheckRow key={r.checkId} result={r} />)}
            </div>
          )}

          {/* Passed checks summary */}
          {report.results.filter(r => r.passed).length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600">
              <CheckCircle2 size={13} />
              <span>
                {report.results.filter(r => r.passed).length} check{report.results.filter(r => r.passed).length !== 1 ? 's' : ''} passed
              </span>
            </div>
          )}
        </div>

        {/* ── Footer actions ── */}
        <div className="border-t border-slate-100 px-5 py-4 flex flex-col gap-2">
          {/* Primary action */}
          {canExportDirectly ? (
            <button
              onClick={onExportAnyway}
              className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold py-2.5 transition-colors"
            >
              Export anyway
            </button>
          ) : (
            <button
              onClick={onClose}
              className="w-full rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold py-2.5 transition-colors"
            >
              Go back and fix issues
            </button>
          )}

          {/* Advanced bypass for blocking issues */}
          {!canExportDirectly && (
            <div>
              {!showAdvancedBypass ? (
                <button
                  onClick={() => setShowAdvancedBypass(true)}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-1.5"
                >
                  Advanced: export despite issues
                </button>
              ) : (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-semibold mb-1.5">⚠ Bypassing certification</p>
                  <p className="leading-relaxed mb-3">
                    This resource has unresolved issues. Exporting anyway may produce a worksheet that is not classroom-ready. This should be used for review drafts only.
                  </p>
                  <button
                    onClick={onExportAnyway}
                    className="w-full rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold py-2 transition-colors"
                  >
                    Export draft anyway (not classroom-ready)
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors py-1"
          >
            Cancel
          </button>
        </div>

      </div>
    </div>
  );
}

/**
 * Small inline banner variant — shown at the top of the resource view when a
 * previous certification run found warnings (not blockers). The teacher dismissed
 * the modal and exported, but the warnings persist in view.
 */
export function CertificationWarningBanner({
  report,
  onDismiss,
}: {
  report: CertificationReport | null;
  onDismiss: () => void;
}) {
  if (!report || (!report.hasWarnings && report.passed)) return null;
  if (!report.passed) return null; // full modal handles blockers

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2 print:hidden"
      role="status"
    >
      <ShieldCheck size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-amber-800">
          Exported with {report.warnings.length} advisor{report.warnings.length !== 1 ? 'ies' : 'y'}
        </p>
        <p className="text-xs text-amber-700 mt-0.5">
          {report.warnings[0]?.message}
          {report.warnings.length > 1 ? ` (+${report.warnings.length - 1} more)` : ''}
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
